import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { getApiClient, useToast } from '@aslanpay/portal-common-lib';

import { DeductionsReportRetryResponse, RetryDeductionsReportRequest } from './types';

const DEDUCTIONS_REPORTS_QUERY_KEY = ['fetchDeductionsReportsPage'];
const DEFAULT_ERROR_DESCRIPTION = 'Sorry, the report could not be retried. Please try again.';

const getErrorDescription = (error: Error) =>
  (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message) ||
  DEFAULT_ERROR_DESCRIPTION;

/** Queues a failed company deductions report to be generated again. */
const useRetryDeductionsReport = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<DeductionsReportRetryResponse, Error, RetryDeductionsReportRequest>(
    async ({ id }) => {
      const response = await getApiClient().post<DeductionsReportRetryResponse>(
        `/reports-service/deductions-reports/${id}/retry`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Report retry queued' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () => queryClient.invalidateQueries({ queryKey: DEDUCTIONS_REPORTS_QUERY_KEY }),
    }
  );
};

export default useRetryDeductionsReport;
